import { type ReactNode } from "react";

import { BTDrawer, type IBTDrawerProps } from "../BTDrawer/BTDrawer";
import { useBottomSheet } from "../BTDrawer/useBottomSheet";

export interface IBTBottomSheetProps
  extends Pick<
    IBTDrawerProps,
    | "visible"
    | "onClose"
    | "destroyOnClose"
    | "zIndex"
    | "keyboard"
    | "role"
    | "aria-label"
  > {
  children: ReactNode;
  /** Optional class name for the bottom sheet */
  className?: string;
  /**
   * Makes the drag handle interactive: drag it down to dismiss the sheet, and tap or press
   * Enter/Space/ArrowDown to close it. Without this the handle is a decorative pill.
   *
   * Opt in per sheet rather than by default, so sheets whose content owns the top of the
   * gesture area aren't given a dismissal path they weren't designed around. Requires
   * `onClose`.
   * @default false
   */
  swipeToDismiss?: boolean;
  "data-testid": string;
}

/**
 * A mobile-optimized bottom sheet component that slides up from the bottom.
 * Wraps BTDrawer with iOS-style bottom sheet styling including:
 * - Rounded top corners
 * - Auto height with max 85vh
 * - Drag handle indicator, optionally draggable via `swipeToDismiss`
 *
 * The behavior itself lives in `useBottomSheet`; this is the ready-made component for the
 * common case of "I want a bottom sheet". A caller that renders its own `BTDrawer` and
 * switches it between presentations should call that hook directly instead — see `BTPeek`.
 */
export function BTBottomSheet({
  children,
  visible,
  onClose,
  className,
  destroyOnClose = true,
  role = "dialog",
  swipeToDismiss = false,
  // The drag handle is decorative and contributes no name, so a role="dialog" with no heading
  // of its own would announce as unlabelled. Sheets that render their own heading can leave
  // this alone; the fallback only applies when nothing else names the sheet.
  "aria-label": ariaLabel = "Bottom sheet",
  "data-testid": testId,
  ...otherProps
}: IBTBottomSheetProps) {
  const sheet = useBottomSheet({
    visible,
    onClose,
    swipeToDismiss,
    className,
  });

  return (
    <BTDrawer
      {...sheet.drawerProps}
      visible={visible ?? false}
      onClose={onClose}
      destroyOnClose={destroyOnClose}
      role={role}
      aria-label={ariaLabel}
      {...otherProps}
    >
      {sheet.handle}
      <div className={sheet.bodyClassName} data-testid={`${testId}-body`}>
        {children}
      </div>
    </BTDrawer>
  );
}
